import { getMomentTz } from "@dexteel/mesf-core";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { WaterBillTrackedGallons } from "../../../models/WaterBilling";
import { getWaterBillTrackedGallons } from "../../../repositories/WaterBillingRepository";

const moment = getMomentTz();

type TrackedGallonsRequest = {
	billId: number;
	startDateKey: string;
	endDateKey: string;
};

type UseTrackedGallonsPreviewArgs = {
	billId: number | null;
	start: Date | null;
	end: Date | null;
	enabled: boolean;
};

export const useTrackedGallonsPreview = ({
	billId,
	start,
	end,
	enabled,
}: UseTrackedGallonsPreviewArgs) => {
	const [request, setRequest] = useState<TrackedGallonsRequest | null>(null);

	const startDateKey =
		start === null ? null : moment.utc(start).format("YYYY-MM-DD");
	const endDateKey = end === null ? null : moment.utc(end).format("YYYY-MM-DD");
	const isRequestable =
		enabled &&
		billId !== null &&
		startDateKey !== null &&
		endDateKey !== null &&
		startDateKey <= endDateKey;

	const query = useQuery<WaterBillTrackedGallons, Error>({
		queryKey: [
			"water-bill-tracked-gallons",
			request?.billId ?? null,
			request?.startDateKey ?? null,
			request?.endDateKey ?? null,
		],
		queryFn: ({ signal }) =>
			getWaterBillTrackedGallons(
				(request as TrackedGallonsRequest).billId,
				(request as TrackedGallonsRequest).startDateKey,
				(request as TrackedGallonsRequest).endDateKey,
				signal,
			),
		enabled: request !== null,
		staleTime: 60_000,
	});

	const isCurrent =
		request !== null &&
		request.billId === billId &&
		request.startDateKey === startDateKey &&
		request.endDateKey === endDateKey;

	const isLoading =
		isRequestable && !query.isError && (!isCurrent || query.isFetching);

	useEffect(() => {
		if (!isRequestable) {
			setRequest(null);
			return;
		}

		const settled: TrackedGallonsRequest = {
			billId: billId as number,
			startDateKey: startDateKey as string,
			endDateKey: endDateKey as string,
		};
		const timer = setTimeout(() => setRequest(settled), 5_000);
		return () => clearTimeout(timer);
	}, [isRequestable, billId, startDateKey, endDateKey]);

	return { tracked: isCurrent ? query.data : undefined, isLoading };
};
